/**
 * useManualStepCompletion
 *
 * Persists "manual" plan step completions (steps whose completionSignal is
 * `{ type: "manual" }`, e.g. the "I understand this" acknowledgement step).
 *
 * Backed by the homepage_state JSONB endpoint — `taskSections.completed` is
 * canonical; `completedPlanSteps` remains synchronized for migration.
 */
import { useCallback, useMemo } from "react";
import {
  getCompletedPlanSteps,
  getTaskSections,
  useHomepageState,
  useMergeHomepageState,
} from "@src/packages/patients/hooks/useHomepageState";

export function useManualStepCompletion() {
  const { data: state } = useHomepageState();
  const taskSections = getTaskSections(state);
  const legacyCompleted = getCompletedPlanSteps(state);
  const completed = useMemo(
    () =>
      Array.from(
        new Set([
          ...Object.entries(taskSections.completed)
            .filter(([, isComplete]) => isComplete)
            .map(([stepId]) => stepId),
          ...legacyCompleted,
        ]),
      ),
    [legacyCompleted, taskSections.completed],
  );
  const { mutate: mergeHomepage } = useMergeHomepageState();

  const setComplete = useCallback(
    (stepId: string, nextComplete: boolean) => {
      const nextLegacyCompleted = nextComplete
        ? Array.from(new Set([...legacyCompleted, stepId]))
        : legacyCompleted.filter((completedId) => completedId !== stepId);

      mergeHomepage({
        // taskSections is now the canonical completion map. Keep the legacy
        // array in sync while existing cached homepage state migrates.
        completedPlanSteps: nextLegacyCompleted,
        taskSections: {
          completed: {
            ...taskSections.completed,
            [stepId]: nextComplete,
          },
          expanded: taskSections.expanded,
        },
      });
    },
    [legacyCompleted, mergeHomepage, taskSections],
  );

  const markComplete = useCallback(
    (stepId: string) => {
      if (completed.includes(stepId)) return;
      setComplete(stepId, true);
    },
    [completed, setComplete],
  );

  const reset = useCallback(() => {
    mergeHomepage({
      completedPlanSteps: [],
      taskSections: {
        completed: {},
        expanded: taskSections.expanded,
      },
    });
  }, [mergeHomepage, taskSections.expanded]);

  return { completed, markComplete, setComplete, reset };
}
